import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DataSyncStatus } from '../DataSyncStatus';
import { Key, ArrowRight, ExternalLink, CheckCircle, AlertCircle } from 'lucide-react';

interface OnboardingPageProps {
  onComplete: () => void;
}

export function OnboardingPage({ onComplete }: OnboardingPageProps) {
  const [apiKey, setApiKey] = useState('');
  const [isValidating, setIsValidating] = useState(false);
  const [error, setError] = useState('');
  const [isApiKeySaved, setIsApiKeySaved] = useState(false);
  const { user } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsValidating(true);

    try {
      const response = await fetch('https://instantly-analytics-api-dev.theo-213.workers.dev/api/user/api-key', {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ instantly_api_key: apiKey }),
      });

      const result = await response.json();

      if (result.success) {
        setIsApiKeySaved(true);
        // Don't immediately start sync - let the user see the status component
      } else {
        setError(result.error || 'Failed to save API key');
      }
    } catch (error) {
      console.error('API key update error:', error);
      setError('Network error. Please try again.');
    } finally {
      setIsValidating(false);
    }
  };

  const handleSkip = () => {
    // Allow users to skip API key setup and go to dashboard
    // They can add it later in settings
    onComplete();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Welcome Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome, {user?.name}! 🎉
          </h1>
          <p className="text-gray-600">
            Let's connect your InstantlyAI account to start analyzing your campaigns.
          </p>
        </div>

        {/* Setup Card */}
        <div className="bg-white/70 backdrop-blur-md rounded-2xl border border-white/20 shadow-xl p-8">
          {/* Progress Steps */}
          <div className="flex items-center justify-center mb-8">
            <div className="flex items-center space-x-4">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 text-white" />
                </div>
                <span className="ml-2 text-sm font-medium text-green-600">Account Created</span>
              </div>
              <div className="w-8 h-0.5 bg-gray-300"></div>
              <div className="flex items-center">
                <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                  <Key className="w-5 h-5 text-white" />
                </div>
                <span className="ml-2 text-sm font-medium text-blue-600">API Setup</span>
              </div>
              <div className="w-8 h-0.5 bg-gray-300"></div>
              <div className="flex items-center">
                <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                  <span className="text-sm font-medium text-gray-600">3</span>
                </div>
                <span className="ml-2 text-sm font-medium text-gray-500">Dashboard</span>
              </div>
            </div>
          </div>

          {/* API Key Setup */}
          <div className="space-y-6">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 mb-2">
                Connect Your InstantlyAI Account
              </h2>
              <p className="text-gray-600 mb-4">
                We need your API key to fetch and analyze your campaign data. Don't worry, it's stored securely and only used to access your InstantlyAI data.
              </p>
            </div>

            {/* How to get API key */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
                <AlertCircle className="w-4 h-4" />
                How to find your API key:
              </h3>
              <ol className="text-sm text-blue-800 space-y-1 ml-6 list-decimal">
                <li>Log in to your InstantlyAI account</li>
                <li>Go to Settings → API & Integrations</li>
                <li>Copy your API key</li>
                <li>Paste it below</li>
              </ol>
              <a
                href="https://app.instantly.ai/app/settings/integrations"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium mt-2"
              >
                Open InstantlyAI Settings
                <ExternalLink className="w-4 h-4" />
              </a>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Error Message */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                  {error}
                </div>
              )}

              {/* API Key Input */}
              <div>
                <label htmlFor="api_key" className="block text-sm font-medium text-gray-700 mb-2">
                  InstantlyAI API Key
                </label>
                <div className="relative">
                  <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    id="api_key"
                    type="password"
                    value={apiKey}
                    onChange={(e) => setApiKey(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white/80 backdrop-blur-sm"
                    placeholder="Paste your InstantlyAI API key here"
                    required
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Your API key is encrypted and stored securely
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  type="submit"
                  disabled={isValidating || !apiKey.trim()}
                  className="flex-1 bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-lg font-medium hover:from-blue-700 hover:to-purple-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-center gap-2"
                >
                  {isValidating ? (
                    <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Setting up your account...
                    </>
                  ) : (
                    <>
                      Connect & Continue
                      <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleSkip}
                  disabled={isValidating}
                  className="sm:w-auto bg-gray-100 text-gray-700 py-3 px-6 rounded-lg font-medium hover:bg-gray-200 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 disabled:opacity-50 transition-all duration-200"
                >
                  Skip for now
                </button>
              </div>
            </form>

            {/* Show sync status if API key is saved */}
            {isApiKeySaved && (
              <div className="mt-6">
                <DataSyncStatus onSyncComplete={onComplete} />
                <div className="mt-4 text-center">
                  <button
                    onClick={onComplete}
                    className="text-gray-600 hover:text-gray-700 text-sm underline"
                  >
                    Continue to dashboard (sync will continue in background)
                  </button>
                </div>
              </div>
            )}

            {/* Security Note */}
            <div className="text-center pt-4 border-t border-gray-200">
              <p className="text-xs text-gray-500">
                🔒 Your data is processed securely and never shared with third parties
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}